import React, { useState, useEffect } from 'react';
import {
  Mail,
  Bell,
  Clock,
  User,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Calendar,
  FileText,
  Users,
  Settings,
  Edit3,
  Eye,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Copy,
  Tag,
  Zap,
  Shield,
  Globe,
  Target,
  AlertCircle,
  Info
} from 'lucide-react';
import api from '../utils/api';
import VariablePicker from './VariablePicker';

const NotificationTemplatesSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [showVariablePicker, setShowVariablePicker] = useState(null);

  // Notification Templates
  const [templates, setTemplates] = useState({
    task_assigned: {
      name: 'Task Assignment',
      subject: 'New Task Assigned: {{task.name}}',
      content: `Hi {{user.name}},

You have been assigned a new task:

**Task:** {{task.name}}
**Priority:** {{task.priority}}
**Due Date:** {{task.due_date}}
**Project:** {{project.name}}
**Client:** {{client.name}}

**Description:**
{{task.description}}

You can view and manage this task in your dashboard.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email', 'in_app'],
      trigger_delay: 0,
      conditions: []
    },
    task_due_reminder: {
      name: 'Task Due Reminder',
      subject: 'Reminder: {{task.name}} is due {{task.due_date}}',
      content: `Hi {{user.name}},

This is a friendly reminder that your task is due soon:

**Task:** {{task.name}}
**Due Date:** {{task.due_date}}
**Priority:** {{task.priority}}
**Project:** {{project.name}}

{{#if task.deliverables}}
**Required Deliverables:**
{{task.deliverables}}
{{/if}}

Please ensure you complete this task on time.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email', 'in_app'],
      trigger_delay: 24, // hours before due date
      conditions: ['task.status != "Done"']
    },
    task_completed: {
      name: 'Task Completion',
      subject: 'Task Completed: {{task.name}}',
      content: `Hi {{client.name}},

Great news! Your task has been completed:

**Task:** {{task.name}}
**Project:** {{project.name}}

{{#if task.review_notes}}
**Team Notes:**
{{task.review_notes}}
{{/if}}

{{#if task.deliverables}}
**Deliverables:**
{{task.deliverables}}
{{/if}}

You can view the results in your client portal.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email'],
      trigger_delay: 0,
      conditions: []
    },
    task_overdue: {
      name: 'Task Overdue Alert',
      subject: 'OVERDUE: {{task.name}} - Action Required',
      content: `Hi {{user.name}},

Your task is now overdue and requires immediate attention:

**Task:** {{task.name}}
**Due Date:** {{task.due_date}} ({{days_overdue}} days ago)
**Priority:** {{task.priority}}
**Project:** {{project.name}}
**Client:** {{client.name}}

Please update the task status or contact your manager if you need assistance.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email', 'in_app'],
      trigger_delay: 0,
      conditions: []
    },
    client_portal_access: {
      name: 'Client Portal Access',
      subject: 'Welcome to {{company.name}} Client Portal',
      content: `Hi {{client.name}},

Welcome to your dedicated client portal! You now have access to:

✓ View your project progress
✓ Track task completion
✓ Review deliverables
✓ Communicate with your team

**Your Portal Details:**
Portal URL: {{portal.url}}
Login Email: {{client.email}}

If you have any questions, feel free to reach out to your project manager.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email'],
      trigger_delay: 0,
      conditions: []
    },
    project_milestone: {
      name: 'Project Milestone',
      subject: 'Milestone Reached: {{milestone.name}}',
      content: `Hi {{client.name}},

Exciting news! We've reached an important milestone in your project:

**Project:** {{project.name}}
**Milestone:** {{milestone.name}}
**Completion Date:** {{milestone.completed_date}}

**Progress Summary:**
{{project.progress_summary}}

{{#if milestone.deliverables}}
**Milestone Deliverables:**
{{milestone.deliverables}}
{{/if}}

We're making great progress and are on track for project completion.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email'],
      trigger_delay: 0,
      conditions: []
    },
    team_mention: {
      name: 'Team Mention',
      subject: 'You were mentioned in {{task.name}}',
      content: `Hi {{user.name}},

You were mentioned in a comment:

**Task:** {{task.name}}
**Project:** {{project.name}}
**Mentioned by:** {{comment.author}}

**Comment:**
"{{comment.content}}"

Click here to view the full conversation and respond.

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email', 'in_app'],
      trigger_delay: 0,
      conditions: []
    },
    meeting_reminder: {
      name: 'Meeting Reminder',
      subject: 'Meeting Reminder: {{meeting.title}}',
      content: `Hi {{user.name}},

This is a reminder for your upcoming meeting:

**Meeting:** {{meeting.title}}
**Date:** {{meeting.date}}
**Time:** {{meeting.time}}
**Duration:** {{meeting.duration}}
{{#if meeting.location}}**Location:** {{meeting.location}}{{/if}}
{{#if meeting.meeting_url}}**Join URL:** {{meeting.meeting_url}}{{/if}}

**Attendees:**
{{meeting.attendees}}

{{#if meeting.agenda}}
**Agenda:**
{{meeting.agenda}}
{{/if}}

Best regards,
{{company.name}} Team`,
      enabled: true,
      delivery_methods: ['email', 'in_app'],
      trigger_delay: 60, // minutes before meeting
      conditions: []
    }
  });

  // Enhanced Settings
  const [settings, setSettings] = useState({
    // Global settings
    company_wide_notifications: true,
    client_notifications: true,
    team_notifications: true,
    
    // Delivery preferences
    default_delivery_method: 'email',
    fallback_delivery_method: 'in_app',
    batch_notifications: false,
    batch_interval: 30, // minutes
    
    // Rate limiting
    rate_limit_enabled: true,
    max_notifications_per_hour: 10,
    max_notifications_per_day: 50,
    
    // Smart features
    smart_delivery_time: true,
    respect_user_timezone: true,
    avoid_quiet_hours: true,
    duplicate_detection: true,
    
    // Integration settings
    slack_webhook_url: '',
    teams_webhook_url: '',
    discord_webhook_url: '',
    
    // Advanced options
    include_company_branding: true,
    use_custom_domain: false,
    custom_domain: '',
    track_email_opens: true,
    track_link_clicks: true
  });

  const [deliveryStats, setDeliveryStats] = useState({
    emails_sent_today: 0,
    emails_sent_this_week: 0,
    open_rate: 0,
    click_rate: 0,
    bounce_rate: 0,
    failed_deliveries: 0
  });

  useEffect(() => {
    loadTemplates();
    loadSettings();
    loadDeliveryStats();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notification-templates');
      
      // Backend returns templates in the format we need
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading notification templates:', error);
      // Keep default templates if API fails
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/notification-settings/company');
      
      // Merge with defaults to ensure all properties exist
      setSettings({ ...settings, ...response.data });
    } catch (error) {
      console.error('Error loading notification settings:', error);
      // Keep default settings if API fails
    }
  };

  const loadDeliveryStats = async () => {
    try {
      const response = await api.get('/notification-stats');
      setDeliveryStats(response.data);
    } catch (error) {
      console.error('Error loading notification stats:', error);
      // Use default stats for fallback
      setDeliveryStats({
        emails_sent_today: 0,
        emails_sent_this_week: 0,
        open_rate: 0,
        click_rate: 0,
        bounce_rate: 0,
        failed_deliveries: 0
      });
    }
  };

  const handleTemplateUpdate = (templateKey, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [templateKey]: {
        ...prev[templateKey],
        [field]: value
      }
    }));
  };

  const handleSettingUpdate = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveTemplate = async (templateKey) => {
    setSaving(true);
    try {
      const templateData = templates[templateKey];
      
      await api.put(`/notification-templates/${templateKey}`, templateData);
      
      // Show success message
      alert(`${templates[templateKey].name} template saved successfully!`);
      
      // Reload templates to get latest data
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert(`Failed to save ${templates[templateKey].name} template. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllSettings = async () => {
    setSaving(true);
    try {
      await api.put('/notification-settings/company', settings);
      
      alert('Notification settings saved successfully!');
      
      // Reload settings to get latest data
      await loadSettings();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Failed to save notification settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTemplate = async (templateKey) => {
    try {
      await api.post(`/notification-templates/${templateKey}/test`, {
        recipient: user.email
      });
      
      alert('Test notification sent successfully! Check your email.');
    } catch (error) {
      console.error('Error sending test notification:', error);
      
      // Show template preview as fallback
      const template = templates[templateKey];
      const previewContent = `Subject: ${template.subject}\n\nContent:\n${template.content}`;
      alert(`Template Preview (Test failed):\n\n${previewContent.substring(0, 500)}${previewContent.length > 500 ? '...' : ''}`);
    }
  };

  const insertVariable = (templateKey, field, variable) => {
    const currentContent = templates[templateKey][field];
    const updatedContent = currentContent + variable;
    handleTemplateUpdate(templateKey, field, updatedContent);
    setShowVariablePicker(null);
  };

  const resetTemplate = async (templateKey) => {
    if (confirm('Are you sure you want to reset this template to default?')) {
      try {
        // Get default templates from the initial state
        const defaultTemplates = {
          task_assigned: {
            name: 'Task Assignment',
            subject: 'New Task Assigned: {{task.name}}',
            content: `Hi {{user.name}},

You have been assigned a new task:

**Task:** {{task.name}}
**Priority:** {{task.priority}}
**Due Date:** {{task.due_date}}
**Project:** {{project.name}}
**Client:** {{client.name}}

**Description:**
{{task.description}}

You can view and manage this task in your dashboard.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email', 'in_app'],
            trigger_delay: 0,
            conditions: []
          },
          task_due_reminder: {
            name: 'Task Due Reminder',
            subject: 'Reminder: {{task.name}} is due {{task.due_date}}',
            content: `Hi {{user.name}},

This is a friendly reminder that your task is due soon:

**Task:** {{task.name}}
**Due Date:** {{task.due_date}}
**Priority:** {{task.priority}}
**Project:** {{project.name}}

{{#if task.deliverables}}
**Required Deliverables:**
{{task.deliverables}}
{{/if}}

Please ensure you complete this task on time.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email', 'in_app'],
            trigger_delay: 24,
            conditions: ['task.status != "Done"']
          },
          task_completed: {
            name: 'Task Completion',
            subject: 'Task Completed: {{task.name}}',
            content: `Hi {{client.name}},

Great news! Your task has been completed:

**Task:** {{task.name}}
**Project:** {{project.name}}

{{#if task.review_notes}}
**Team Notes:**
{{task.review_notes}}
{{/if}}

{{#if task.deliverables}}
**Deliverables:**
{{task.deliverables}}
{{/if}}

You can view the results in your client portal.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email'],
            trigger_delay: 0,
            conditions: []
          },
          task_overdue: {
            name: 'Task Overdue Alert',
            subject: 'OVERDUE: {{task.name}} - Action Required',
            content: `Hi {{user.name}},

Your task is now overdue and requires immediate attention:

**Task:** {{task.name}}
**Due Date:** {{task.due_date}} ({{days_overdue}} days ago)
**Priority:** {{task.priority}}
**Project:** {{project.name}}
**Client:** {{client.name}}

Please update the task status or contact your manager if you need assistance.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email', 'in_app'],
            trigger_delay: 0,
            conditions: []
          },
          client_portal_access: {
            name: 'Client Portal Access',
            subject: 'Welcome to {{company.name}} Client Portal',
            content: `Hi {{client.name}},

Welcome to your dedicated client portal! You now have access to:

✓ View your project progress
✓ Track task completion
✓ Review deliverables
✓ Communicate with your team

**Your Portal Details:**
Portal URL: {{portal.url}}
Login Email: {{client.email}}

If you have any questions, feel free to reach out to your project manager.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email'],
            trigger_delay: 0,
            conditions: []
          },
          project_milestone: {
            name: 'Project Milestone',
            subject: 'Milestone Reached: {{milestone.name}}',
            content: `Hi {{client.name}},

Exciting news! We've reached an important milestone in your project:

**Project:** {{project.name}}
**Milestone:** {{milestone.name}}
**Completion Date:** {{milestone.completed_date}}

**Progress Summary:**
{{project.progress_summary}}

{{#if milestone.deliverables}}
**Milestone Deliverables:**
{{milestone.deliverables}}
{{/if}}

We're making great progress and are on track for project completion.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email'],
            trigger_delay: 0,
            conditions: []
          },
          team_mention: {
            name: 'Team Mention',
            subject: 'You were mentioned in {{task.name}}',
            content: `Hi {{user.name}},

You were mentioned in a comment:

**Task:** {{task.name}}
**Project:** {{project.name}}
**Mentioned by:** {{comment.author}}

**Comment:**
"{{comment.content}}"

Click here to view the full conversation and respond.

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email', 'in_app'],
            trigger_delay: 0,
            conditions: []
          },
          meeting_reminder: {
            name: 'Meeting Reminder',
            subject: 'Meeting Reminder: {{meeting.title}}',
            content: `Hi {{user.name}},

This is a reminder for your upcoming meeting:

**Meeting:** {{meeting.title}}
**Date:** {{meeting.date}}
**Time:** {{meeting.time}}
**Duration:** {{meeting.duration}}
{{#if meeting.location}}**Location:** {{meeting.location}}{{/if}}
{{#if meeting.meeting_url}}**Join URL:** {{meeting.meeting_url}}{{/if}}

**Attendees:**
{{meeting.attendees}}

{{#if meeting.agenda}}
**Agenda:**
{{meeting.agenda}}
{{/if}}

Best regards,
{{company.name}} Team`,
            enabled: true,
            delivery_methods: ['email', 'in_app'],
            trigger_delay: 60,
            conditions: []
          }
        };
        
        if (defaultTemplates[templateKey]) {
          // Update the template to default and save it
          await api.put(`/notification-templates/${templateKey}`, defaultTemplates[templateKey]);
          
          // Update local state
          setTemplates(prev => ({
            ...prev,
            [templateKey]: defaultTemplates[templateKey]
          }));
          
          alert('Template reset to default successfully!');
        }
      } catch (error) {
        console.error('Error resetting template:', error);
        alert('Failed to reset template. Please try again.');
      }
    }
  };

  const tabs = [
    { id: 'templates', name: 'Email Templates', icon: Mail },
    { id: 'settings', name: 'Notification Settings', icon: Settings },
    { id: 'analytics', name: 'Analytics & Stats', icon: Target }
  ];

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600">Loading notification templates...</span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notification Templates</h2>
          <p className="text-gray-600">Customize notification templates and delivery settings</p>
        </div>
        <button
          onClick={handleSaveAllSettings}
          disabled={saving || loading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save All Settings'}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="grid gap-6">
            {Object.entries(templates).map(([key, template]) => (
              <div key={key} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${template.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      <div className="flex items-center space-x-2">
                        {template.delivery_methods.map(method => (
                          <span key={method} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {method === 'email' && <Mail className="w-3 h-3 mr-1" />}
                            {method === 'in_app' && <Bell className="w-3 h-3 mr-1" />}
                            {method}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleTestTemplate(key)}
                        className="flex items-center space-x-1 px-3 py-1 text-blue-600 hover:text-blue-700 text-sm border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Test</span>
                      </button>
                      <button
                        onClick={() => setExpandedTemplate(expandedTemplate === key ? null : key)}
                        className="flex items-center space-x-1 px-3 py-1 text-gray-600 hover:text-gray-700 text-sm border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                        <span>{expandedTemplate === key ? 'Collapse' : 'Edit'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {expandedTemplate === key && (
                  <div className="p-6 space-y-6">
                    {/* Template Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={template.enabled}
                            onChange={(e) => handleTemplateUpdate(key, 'enabled', e.target.checked)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Enable this notification</span>
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Trigger Delay (minutes/hours)
                        </label>
                        <input
                          type="number"
                          value={template.trigger_delay}
                          onChange={(e) => handleTemplateUpdate(key, 'trigger_delay', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Subject Line */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Email Subject</label>
                        <button
                          onClick={() => setShowVariablePicker(showVariablePicker === `${key}-subject` ? null : `${key}-subject`)}
                          className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-700 text-xs border border-purple-200 rounded hover:bg-purple-50 transition-colors"
                        >
                          <Tag className="w-3 h-3" />
                          <span>Variables</span>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={template.subject}
                        onChange={(e) => handleTemplateUpdate(key, 'subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Enter email subject..."
                      />
                      {showVariablePicker === `${key}-subject` && (
                        <div className="mt-2">
                          <VariablePicker
                            onVariableSelect={(variable) => insertVariable(key, 'subject', variable.variable)}
                            showPreview={false}
                          />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Email Content</label>
                        <button
                          onClick={() => setShowVariablePicker(showVariablePicker === `${key}-content` ? null : `${key}-content`)}
                          className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-700 text-xs border border-purple-200 rounded hover:bg-purple-50 transition-colors"
                        >
                          <Tag className="w-3 h-3" />
                          <span>Variables</span>
                        </button>
                      </div>
                      <textarea
                        value={template.content}
                        onChange={(e) => handleTemplateUpdate(key, 'content', e.target.value)}
                        rows={12}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter email content..."
                      />
                      {showVariablePicker === `${key}-content` && (
                        <div className="mt-2">
                          <VariablePicker
                            onVariableSelect={(variable) => insertVariable(key, 'content', variable.variable)}
                            showPreview={false}
                          />
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <button
                        onClick={() => resetTemplate(key)}
                        className="flex items-center space-x-2 px-3 py-1 text-gray-600 hover:text-gray-700 text-sm border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Reset to Default</span>
                      </button>
                      
                      <button
                        onClick={() => handleSaveTemplate(key)}
                        disabled={saving}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        <span>{saving ? 'Saving...' : 'Save Template'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-8">
          {/* Global Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Notification Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={settings.company_wide_notifications}
                    onChange={(e) => handleSettingUpdate('company_wide_notifications', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Company-wide Notifications</span>
                    <p className="text-xs text-gray-500">Enable notifications for all team members</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={settings.client_notifications}
                    onChange={(e) => handleSettingUpdate('client_notifications', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Client Notifications</span>
                    <p className="text-xs text-gray-500">Send notifications to clients</p>
                  </div>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={settings.team_notifications}
                    onChange={(e) => handleSettingUpdate('team_notifications', e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Team Notifications</span>
                    <p className="text-xs text-gray-500">Internal team notifications</p>
                  </div>
                </label>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Delivery Method</label>
                  <select
                    value={settings.default_delivery_method}
                    onChange={(e) => handleSettingUpdate('default_delivery_method', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="in_app">In-App</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Batch Interval (minutes)</label>
                  <input
                    type="number"
                    value={settings.batch_interval}
                    onChange={(e) => handleSettingUpdate('batch_interval', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min="1"
                    max="1440"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Rate Limiting */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limiting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Notifications per Hour</label>
                <input
                  type="number"
                  value={settings.max_notifications_per_hour}
                  onChange={(e) => handleSettingUpdate('max_notifications_per_hour', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Notifications per Day</label>
                <input
                  type="number"
                  value={settings.max_notifications_per_day}
                  onChange={(e) => handleSettingUpdate('max_notifications_per_day', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Smart Features */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Smart Features</h3>
            <div className="space-y-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.smart_delivery_time}
                  onChange={(e) => handleSettingUpdate('smart_delivery_time', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Smart Delivery Time</span>
                  <p className="text-xs text-gray-500">Optimize delivery times based on user activity</p>
                </div>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.respect_user_timezone}
                  onChange={(e) => handleSettingUpdate('respect_user_timezone', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Respect User Timezone</span>
                  <p className="text-xs text-gray-500">Send notifications in user's local time</p>
                </div>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.duplicate_detection}
                  onChange={(e) => handleSettingUpdate('duplicate_detection', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Duplicate Detection</span>
                  <p className="text-xs text-gray-500">Prevent sending duplicate notifications</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <Mail className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{deliveryStats.emails_sent_today}</p>
                  <p className="text-sm text-gray-600">Emails Today</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <Target className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{deliveryStats.open_rate}%</p>
                  <p className="text-sm text-gray-600">Open Rate</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <Zap className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{deliveryStats.click_rate}%</p>
                  <p className="text-sm text-gray-600">Click Rate</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{deliveryStats.failed_deliveries}</p>
                  <p className="text-sm text-gray-600">Failed</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Notification analytics coming soon</p>
              <p className="text-sm">Track open rates, click-through rates, and delivery statistics</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationTemplatesSettings;